import {
  timestamp,
  text,
  jsonb,
  varchar,
  boolean,
  integer,
  unique,
} from "drizzle-orm/pg-core"
import { id_length, ulidId } from "./shared"
import {
  partyRegistration,
  supportingDocumentFile,
  exportRequest,
  coreProductItem,
  transferRequest,
  coreExportSchema,
} from "./schema"

export const supportingDocument = coreExportSchema.table(
  "supporting_document",
  {
    id: ulidId(), // format should be sdd-<ulid>
    currentDocumentVersion: varchar("current_document_version_id"),
    status: text("status", {
      enum: ["ACTIVE", "REPLACED", "CANCELLED"],
    }),
    documentType: text("document_type", {
      enum: [
        "FIC",
        "IP",
        "PAC",
        "ADH",
        "ADM",
        "INSP",
        "DOC",
        "OPC",
        "TRT",
        "ORG",
        "OMA",
        "SLAB",
        "OTHER",
      ], // enum updated based on Decision 142761
    }),
    documentNumber: varchar("document_number", { length: 50 }),
    documentSequence: integer("document_sequence"),
    documentReferenceNumber: text("document_reference_number"),
    primaryOperator: varchar("primary_operator", {
      length: id_length,
    }).references(() => partyRegistration.id, { onDelete: "cascade" }),
    primaryOperatorBlob: jsonb("primary_operator_blob"),
    intendedOperators: jsonb("intended_operators"), // array of registration ids
    originCountry: jsonb("origin_country"),
    reason: text("reason"),
    productDescription: text("product_description"),
    identificationMarks: jsonb("identification_marks"), // array of marks
    foreignManufacturePremisesIds: jsonb("foreign_manufacture_premises_ids"), // array of premises ids
    nzManufacturePremiseId: varchar("nz_manufacture_premises_id", {
      length: id_length,
    }).references(() => partyRegistration.id, { onDelete: "cascade" }),
    nzManufacturePremisesName: text("nz_manufacture_premises_name"),
    foreignManufacturePremisesNames: text("foreign_manufacture_premises_names"),
    productionDates: jsonb("production_dates"), // [{ from: date, to: date }] GraphQl [CoreDateRange]
    fileId: varchar("supporting_document_file_id", { length: id_length })
      .notNull()
      .references(() => supportingDocumentFile.id, { onDelete: "cascade" }),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    createdBy: varchar("created_by", { length: id_length }), // ID for user
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedBy: varchar("updated_by", { length: id_length }), // ID for user
    isLinked: boolean("is_linked").default(false).notNull(), // true when linked to product
  },
)

export const supportingDocumentSequence = coreExportSchema.table(
  "supporting_document_sequence",
  {
    id: ulidId(),
    year: integer("year").notNull(),
    documentType: text("document_type").notNull(),
    currentSequence: integer("current_sequence").notNull().default(0),
    createdAt: timestamp("created_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
    updatedAt: timestamp("updated_at", { withTimezone: true })
      .defaultNow()
      .notNull(),
  },
  (table) => ({
    uniqueYearTypeSequence: unique("unique_year_type_sequence").on(
      table.year,
      table.documentType,
      table.currentSequence,
    ),
  }),
)

export const supportingDocumentReferenceExportCertificate =
  coreExportSchema.table("supporting_document_ref_export", {
    id: ulidId(), // format should be sdc-<ulid>
    supportingDocumentId: varchar("supporting_document_id", {
      length: id_length,
    }).references(() => supportingDocument.id, { onDelete: "cascade" }),
    exportCertificateId: varchar("export_certificate_id", {
      length: id_length,
    }).references(() => exportRequest.id, { onDelete: "cascade" }),
    comment: text("comment").default(""),
  })

export const supportingDocumentReferenceProduct = coreExportSchema.table(
  "supporting_document_ref_product",
  {
    id: ulidId(), // format should be sdp-<ulid>
    supportingDocumentId: varchar("supporting_document_id", {
      length: id_length,
    }).references(() => supportingDocument.id, { onDelete: "cascade" }),
    productId: varchar("product_id", {
      length: id_length,
    }).references(() => coreProductItem.id, { onDelete: "cascade" }),
    comment: text("comment").default(""),
    isInherited: boolean("is_inherited").default(false).notNull(),
  },
)

export const supportingDocumentReferenceTransfer = coreExportSchema.table(
  "supporting_document_ref_transfer",
  {
    id: ulidId(), // format should be sdt-<ulid>
    supportingDocumentId: varchar("supporting_document_id", {
      length: id_length,
    }).references(() => supportingDocument.id, { onDelete: "cascade" }),
    transferRequestId: varchar("transfer_request_id", {
      length: id_length,
    }).references(() => transferRequest.id, { onDelete: "cascade" }),
    comment: text("comment").default(""),
  },
)
